import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Lock, Users, HeartPulse, Mail, X, ArrowRight, Sparkles } from "lucide-react";

const KEY = "ks_tour_seen";

export default function Onboarding() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const seen = localStorage.getItem(KEY);
    if (!seen) {
      const tm = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(tm);
    }
  }, []);

  const finish = (action) => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
    if (action === "start") navigate("/vault");
  };

  if (!open) return null;

  const steps = [
    { icon: Lock, t: t("tour_step1_t"), d: t("tour_step1_d") },
    { icon: Mail, t: t("tour_step2_t"), d: t("tour_step2_d") },
    { icon: Users, t: t("tour_step3_t"), d: t("tour_step3_d") },
    { icon: HeartPulse, t: t("tour_step4_t"), d: t("tour_step4_d") },
  ];

  const isLast = step === steps.length - 1;
  const Cur = steps[step];
  const CurIcon = Cur.icon;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="onboarding-tour">
      <div className="absolute inset-0 bg-obsidian/90 backdrop-blur-md" />
      <div className="glass-card rounded-md p-8 md:p-12 max-w-lg w-full relative gold-glow fade-in">
        <button
          onClick={() => finish("skip")}
          className="absolute top-4 right-4 text-faded hover:text-muted text-xs flex items-center gap-1"
          data-testid="tour-skip"
        >
          <X className="w-3 h-3" /> {t("tour_skip")}
        </button>

        {step === 0 && (
          <div className="text-center mb-6">
            <div className="font-label text-gold mb-3 flex items-center justify-center gap-2">
              <Sparkles className="w-3 h-3" /> {t("tour_title")}
            </div>
            <p className="text-muted text-sm">{t("tour_sub")}</p>
          </div>
        )}

        <div className="text-center my-8 fade-in" key={step}>
          <div className="inline-flex p-5 rounded-full bg-gold/10 border border-gold-subtle mb-6">
            <CurIcon className="w-10 h-10 text-gold" strokeWidth={1.2} />
          </div>
          <h2 className="font-display text-3xl text-gold-light mb-4">{Cur.t}</h2>
          <p className="text-muted leading-relaxed font-body">{Cur.d}</p>
        </div>

        <div className="flex items-center justify-center gap-1.5 mt-6 mb-8">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all ${
                i === step ? "w-8 bg-gold" : "w-2 bg-gold-subtle"
              }`}
              style={{ background: i <= step ? "#C9A36A" : "rgba(201,163,106,0.2)" }}
            />
          ))}
        </div>

        <div className="flex gap-3 justify-end">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} className="btn-ghost" data-testid="tour-back">
              {t("tour_back")}
            </button>
          )}
          {!isLast ? (
            <button
              onClick={() => setStep(step + 1)}
              className="btn-gold"
              data-testid="tour-next"
            >
              {t("tour_next")} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => finish("start")}
              className="btn-gold"
              data-testid="tour-start"
            >
              {t("tour_cta")} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
